import { useEffect, useRef } from "react";
import { AdvancedMarker, APIProvider, Map as GoogleMap, useMap } from "@vis.gl/react-google-maps";
import type { JobsiteServiceAreaBoundary } from "../../types/runtime-config";

// ─── Boundary colours (matches dashboard palette) ───────────────────────────

const BOUNDARY_COLORS = ["#3b82f6", "#6366f1", "#8b5cf6", "#a855f7", "#ec4899", "#06b6d4"];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function ringToPath(ring: number[][]): { lat: number; lng: number }[] {
  return ring.map(([lng, lat]) => ({ lat, lng }));
}

function computeBounds(
  geometries: JobsiteServiceAreaBoundary[],
): { north: number; south: number; east: number; west: number } | null {
  let north = -Infinity,
    south = Infinity,
    east = -Infinity,
    west = Infinity;
  let hasCoords = false;

  function processRing(ring: number[][]) {
    for (const [lng, lat] of ring) {
      if (lat > north) north = lat;
      if (lat < south) south = lat;
      if (lng > east) east = lng;
      if (lng < west) west = lng;
      hasCoords = true;
    }
  }

  for (const geom of geometries) {
    if (geom.type === "Polygon") {
      for (const ring of geom.coordinates as number[][][]) processRing(ring);
    } else {
      for (const polygon of geom.coordinates as number[][][][]) {
        for (const ring of polygon) processRing(ring);
      }
    }
  }

  return hasCoords ? { north, south, east, west } : null;
}

// ─── Polygon overlay (imperative, same pattern as dashboard) ─────────────────

interface BoundaryPolygonProps {
  geometry: JobsiteServiceAreaBoundary;
  colorIndex: number;
}

function BoundaryPolygon({ geometry, colorIndex }: BoundaryPolygonProps) {
  const map = useMap();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const polygonsRef = useRef<any[]>([]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!map || typeof (globalThis as any).google === "undefined") return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const g = (globalThis as any).google;

    for (const p of polygonsRef.current) p.setMap(null);
    polygonsRef.current = [];

    const color = BOUNDARY_COLORS[colorIndex % BOUNDARY_COLORS.length];
    const opts = {
      fillColor: color,
      fillOpacity: 0.15,
      strokeColor: color,
      strokeOpacity: 0.7,
      strokeWeight: 2,
      map,
      clickable: false,
    };

    if (geometry.type === "Polygon") {
      const paths = (geometry.coordinates as number[][][]).map(ringToPath);
      polygonsRef.current.push(new g.maps.Polygon({ paths, ...opts }));
    } else {
      for (const polygonCoords of geometry.coordinates as number[][][][]) {
        const paths = polygonCoords.map(ringToPath);
        polygonsRef.current.push(new g.maps.Polygon({ paths, ...opts }));
      }
    }

    return () => {
      for (const p of polygonsRef.current) p.setMap(null);
      polygonsRef.current = [];
    };
  }, [map, geometry, colorIndex]);

  return null;
}

// ─── Fit viewport to all boundaries ─────────────────────────────────────────

function FitToBounds({
  bounds,
}: {
  bounds: { north: number; south: number; east: number; west: number };
}) {
  const map = useMap();

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!map || typeof (globalThis as any).google === "undefined") return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const g = (globalThis as any).google;
    const latLngBounds = new g.maps.LatLngBounds(
      { lat: bounds.south, lng: bounds.west },
      { lat: bounds.north, lng: bounds.east },
    );
    map.fitBounds(latLngBounds, 32);
  }, [map, bounds]);

  return null;
}

// ─── Fallback marker for areas without boundary data ─────────────────────────

/**
 * Outer diameter of the dot. The old `SymbolPath.CIRCLE` at scale 8 drew a 16px
 * circle with a 2px stroke straddling its edge, so the white ring ran from 14px
 * to 18px — which a 18px border-box div with a 2px border reproduces exactly.
 */
const MARKER_SIZE = 18;

/**
 * `google.maps.Marker` was deprecated in Feb 2024 and logs a console warning on
 * every deployed jobsite, so the dot is drawn as `AdvancedMarker` content
 * instead. Advanced markers anchor custom content by its bottom edge, whereas
 * the legacy symbol was centred on the coordinate — hence the half-height
 * nudge, which keeps the dot exactly where it used to sit.
 */
export function FallbackMarker({ lat, lng }: { lat: number; lng: number }) {
  return (
    <AdvancedMarker position={{ lat, lng }}>
      <div
        style={{
          width: MARKER_SIZE,
          height: MARKER_SIZE,
          boxSizing: "border-box",
          borderRadius: "50%",
          // `cc` is the old fillOpacity of 0.8. Element opacity would fade the
          // white ring too, which the legacy stroke never was.
          backgroundColor: `${BOUNDARY_COLORS[0]}cc`,
          border: "2px solid #fff",
          transform: `translateY(${MARKER_SIZE / 2}px)`,
        }}
      />
    </AdvancedMarker>
  );
}

// ─── Public interface ─────────────────────────────────────────────────────────

export interface ServiceAreaMapItem {
  id?: string;
  displayName: string;
  latitude?: number | null;
  longitude?: number | null;
  boundary?: JobsiteServiceAreaBoundary | null;
}

interface ServiceAreaMapProps {
  apiKey: string;
  areas: ServiceAreaMapItem[];
  height?: number;
}

function MapContent({ areas }: { areas: ServiceAreaMapItem[] }) {
  const areasWithBoundary = areas.filter((a) => a.boundary);
  const areasWithCoords = areas.filter((a) => !a.boundary && a.latitude && a.longitude);

  const bounds =
    areasWithBoundary.length > 0 ? computeBounds(areasWithBoundary.map((a) => a.boundary!)) : null;

  return (
    <>
      {areasWithBoundary.map((area, i) => (
        <BoundaryPolygon key={area.id ?? i} geometry={area.boundary!} colorIndex={i} />
      ))}
      {areasWithCoords.map((area) => (
        <FallbackMarker
          key={area.id ?? area.displayName}
          lat={area.latitude!}
          lng={area.longitude!}
        />
      ))}
      {bounds && <FitToBounds bounds={bounds} />}
    </>
  );
}

export function ServiceAreaMap({ apiKey, areas, height = 400 }: ServiceAreaMapProps) {
  const firstWithCoords = areas.find((a) => a.latitude && a.longitude);
  const defaultCenter = firstWithCoords
    ? { lat: firstWithCoords.latitude!, lng: firstWithCoords.longitude! }
    : { lat: 39.5, lng: -98.35 }; // geographic center of US as fallback

  return (
    <div style={{ height, width: "100%", display: "block" }}>
      <APIProvider apiKey={apiKey}>
        <GoogleMap
          defaultCenter={defaultCenter}
          defaultZoom={10}
          mapId="jobsite-service-area-map"
          gestureHandling="cooperative"
          disableDefaultUI={false}
          style={{ width: "100%", height: "100%" }}
        >
          <MapContent areas={areas} />
        </GoogleMap>
      </APIProvider>
    </div>
  );
}
